import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Field, inputClass, selectClass } from './BackgroundFields';
import { formatPriceTtc, groupItemsByCategoryName, newClickCollectItem } from '../utils/commerce';
import { FR_VAT_RATES, normalizeClickCollectItemPricing } from '../utils/commercePricing';
import { SUGGESTED_MENU_CATEGORIES } from '../utils/commerceDisplay';
import { pendingUnitsForItem, saleQuantityAvailable } from '../utils/commerceAdmin';

export default function ClickCollectMenuCatalogEditor({
  items,
  orders,
  onItemsChange,
  onPatchItem,
  onAddImages,
  layout,
  onLayoutChange,
}) {
  const [customCategory, setCustomCategory] = useState('');
  const [activeCategory, setActiveCategory] = useState('');

  const categories = useMemo(() => groupItemsByCategoryName(items), [items]);

  useEffect(() => {
    if (!categories.length) {
      setActiveCategory('');
      return;
    }
    const names = categories.map((c) => c.name);
    if (!activeCategory || !names.includes(activeCategory)) {
      setActiveCategory(names[0]);
    }
  }, [categories, activeCategory]);

  const setItems = (updater) => {
    onItemsChange(typeof updater === 'function' ? updater(items) : updater);
  };

  const addCategoryWithProduct = (categoryName) => {
    const cat = String(categoryName || '').trim();
    if (!cat) return;
    const item = newClickCollectItem({ category: cat, quantityAvailable: 10 });
    setItems((prev) => [...prev, item]);
    setActiveCategory(cat);
  };

  const addProductToCategory = (categoryName) => {
    const cat = String(categoryName || '').trim() || 'Sans catégorie';
    setItems((prev) => [
      ...prev,
      newClickCollectItem({ category: cat, quantityAvailable: 10 }),
    ]);
    setActiveCategory(cat);
  };

  const renameCategory = (oldName, newName) => {
    const next = String(newName || '').trim();
    if (!next || next === oldName) return;
    setItems((prev) =>
      prev.map((it) =>
        (String(it.category || '').trim() || 'Sans catégorie') === oldName
          ? { ...it, category: next }
          : it,
      ),
    );
    if (activeCategory === oldName) setActiveCategory(next);
  };

  const removeCategory = (categoryName) => {
    if (
      !window.confirm(
        `Supprimer la catégorie « ${categoryName} » et tous ses produits ?`,
      )
    ) {
      return;
    }
    setItems((prev) =>
      prev.filter(
        (it) => (String(it.category || '').trim() || 'Sans catégorie') !== categoryName,
      ),
    );
    if (activeCategory === categoryName) setActiveCategory('');
  };

  const existingNames = useMemo(
    () => new Set(categories.map((c) => c.name)),
    [categories],
  );

  const suggestedMissing = SUGGESTED_MENU_CATEGORIES.filter((c) => !existingNames.has(c));

  if (!items.length) {
    return (
      <div className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-6 sm:p-8 space-y-5">
        <div className="text-center max-w-lg mx-auto space-y-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Icon name="UtensilsCrossed" size={22} className="text-primary" />
          </div>
          <h3 className="text-base font-semibold text-foreground">Créez votre carte en 2 minutes</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Commencez par une section (Entrées, Plats…). Nous ajoutons un premier produit à
            remplir — vous pourrez en ajouter d&apos;autres ensuite.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {SUGGESTED_MENU_CATEGORIES.map((cat) => (
            <Button key={cat} type="button" size="sm" onClick={() => addCategoryWithProduct(cat)}>
              <Icon name="Plus" size={14} className="mr-1" />
              {cat}
            </Button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
          <input
            className={inputClass}
            placeholder="Ou nom de catégorie…"
            value={customCategory}
            onChange={(e) => setCustomCategory(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customCategory.trim()) {
                addCategoryWithProduct(customCategory.trim());
                setCustomCategory('');
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            disabled={!customCategory.trim()}
            onClick={() => {
              addCategoryWithProduct(customCategory.trim());
              setCustomCategory('');
            }}
          >
            Créer
          </Button>
        </div>
        <Field label="Type d'affichage client">
          <select className={selectClass} value={layout} onChange={(e) => onLayoutChange(e.target.value)}>
            <option value="classic">Carte restaurant (recommandé)</option>
            <option value="gallery">Grille avec photos</option>
          </select>
        </Field>
      </div>
    );
  }

  const activeBlock = categories.find((c) => c.name === activeCategory) || categories[0];
  const activeName = activeBlock?.name ?? '';
  const catItems = activeBlock?.items ?? [];

  return (
    <div className="w-full flex flex-col lg:flex-row gap-4 items-stretch min-h-0">
      <aside className="w-full lg:w-52 xl:w-56 shrink-0 rounded-xl border border-border bg-card p-1.5 space-y-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-2 pt-1 pb-0.5">
          Catégories
        </p>
        {categories.map(({ name, items: list }) => (
          <button
            key={name}
            type="button"
            onClick={() => setActiveCategory(name)}
            className={`w-full flex items-center justify-between gap-2 px-2.5 py-2 text-sm rounded-lg text-left transition-colors ${
              activeName === name
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-foreground hover:bg-muted/60'
            }`}
          >
            <span className="truncate">{name}</span>
            <span className="text-xs tabular-nums text-muted-foreground shrink-0">{list.length}</span>
          </button>
        ))}
        {suggestedMissing.length ? (
          <div className="border-t border-border pt-2 mt-1 px-1 space-y-1">
            <p className="text-[10px] text-muted-foreground px-1">Suggestions</p>
            {suggestedMissing.map((cat) => (
              <button
                key={cat}
                type="button"
                className="w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-muted/60 text-muted-foreground"
                onClick={() => addCategoryWithProduct(cat)}
              >
                + {cat}
              </button>
            ))}
          </div>
        ) : null}
        <div className="border-t border-border pt-2 px-1 flex gap-1">
          <input
            className={`${inputClass} text-xs py-1.5 min-w-0 flex-1`}
            placeholder="Nouvelle…"
            value={customCategory}
            onChange={(e) => setCustomCategory(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customCategory.trim()) {
                addCategoryWithProduct(customCategory.trim());
                setCustomCategory('');
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 px-2"
            disabled={!customCategory.trim()}
            onClick={() => {
              addCategoryWithProduct(customCategory.trim());
              setCustomCategory('');
            }}
            title="Nouvelle catégorie"
          >
            <Icon name="Plus" size={14} />
          </Button>
        </div>
      </aside>

      {activeBlock ? (
        <div className="flex-1 min-w-0 w-full rounded-xl border border-border bg-card overflow-hidden flex flex-col min-h-[min(65vh,640px)]">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-muted/25 border-b border-border">
            <input
              className={`${inputClass} flex-1 min-w-[140px] font-semibold`}
              value={activeName === 'Sans catégorie' ? '' : activeName}
              placeholder="Nom de la catégorie"
              onChange={(e) => renameCategory(activeName, e.target.value || 'Sans catégorie')}
            />
            <Button type="button" size="sm" variant="outline" onClick={() => addProductToCategory(activeName)}>
              <Icon name="Plus" size={14} className="mr-1" />
              Produit
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="text-destructive"
              onClick={() => removeCategory(activeName)}
            >
              <Icon name="Trash2" size={14} />
            </Button>
          </div>
          <div className="p-4 space-y-3 flex-1 min-h-0 overflow-y-auto">
            {catItems.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                Aucun produit — cliquez sur « Produit » pour commencer.
              </p>
            ) : (
              catItems.map((item, idx) => (
                <ProductRow
                  key={item.id}
                  item={item}
                  index={idx}
                  layout={layout}
                  orders={orders}
                  onPatch={onPatchItem}
                  onRemove={() => setItems((prev) => prev.filter((it) => it.id !== item.id))}
                  onAddImages={onAddImages}
                />
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ProductRow({ item, index, layout, orders, onPatch, onRemove, onAddImages }) {
  const forSale = saleQuantityAvailable(item, orders);
  const pending = pendingUnitsForItem(orders, item.id);
  const step = index + 1;
  const pricing = normalizeClickCollectItemPricing(item);

  const patchPricing = (patch) => {
    onPatch(item.id, normalizeClickCollectItemPricing({ ...item, ...patch }));
  };

  return (
    <div className="rounded-lg border border-border/80 p-4 space-y-3 bg-background">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-primary">Produit {step}</p>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={item.active !== false}
            onChange={(e) => onPatch(item.id, { active: e.target.checked })}
          />
          Visible sur la carte
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nom du plat *" hint="Affiché sur la carte client">
          <input
            className={inputClass}
            value={item.title}
            placeholder="Ex. Salade du chef"
            onChange={(e) => onPatch(item.id, { title: e.target.value })}
          />
        </Field>
        <Field label="Prix HT (€)" hint="Hors taxes — TTC calculé automatiquement">
          <input
            type="number"
            min="0"
            step="0.01"
            className={inputClass}
            value={pricing.priceHt ?? 0}
            onChange={(e) => patchPricing({ priceHt: Number(e.target.value) || 0 })}
          />
        </Field>
        <Field label="Taux de TVA">
          <select
            className={selectClass}
            value={pricing.vatRate ?? 10}
            onChange={(e) => patchPricing({ vatRate: Number(e.target.value) })}
          >
            {FR_VAT_RATES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">
        Prix TTC affiché client :{' '}
        <strong className="text-foreground">{formatPriceTtc(pricing.priceTtc)}</strong>
      </p>
      <Field label="Description courte" hint="Optionnel — sous le nom sur la carte">
        <textarea
          className={`${inputClass} min-h-[56px]`}
          value={item.description || ''}
          placeholder="Ingrédients, allergènes…"
          onChange={(e) => onPatch(item.id, { description: e.target.value })}
        />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Stock disponible" hint="Nombre de portions commandables">
          <input
            type="number"
            min="0"
            className={inputClass}
            value={item.quantityAvailable ?? 0}
            onChange={(e) =>
              onPatch(item.id, {
                quantityAvailable: Math.max(0, Math.floor(Number(e.target.value) || 0)),
              })
            }
          />
        </Field>
        <div className="text-xs rounded-lg bg-muted/30 p-3 flex flex-col justify-center gap-1">
          <span className="text-muted-foreground">
            En vente : <strong className="text-foreground">{forSale}</strong>
            {pending > 0 ? ` (${pending} en attente)` : ''}
          </span>
          {item.title && pricing.priceTtc > 0 ? (
            <span className="text-emerald-700">
              Aperçu : {formatPriceTtc(pricing.priceTtc)} TTC (HT {formatPriceTtc(pricing.priceHt)})
            </span>
          ) : (
            <span className="text-amber-700">Complétez le nom et le prix</span>
          )}
        </div>
      </div>
      {layout === 'gallery' ? (
        <Field label="Photo">
          <input
            type="file"
            accept="image/*"
            multiple
            className="text-xs"
            onChange={(e) => {
              onAddImages(item.id, e.target.files);
              e.target.value = '';
            }}
          />
        </Field>
      ) : null}
      <div className="flex justify-end">
        <Button type="button" size="sm" variant="ghost" className="text-destructive" onClick={onRemove}>
          Supprimer ce produit
        </Button>
      </div>
    </div>
  );
}
